import React, { useState } from 'react';

const CampDataUpload = () => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.toLowerCase().endsWith('.csv')) {
                setError('Please select a CSV file');
                setFile(null);
                return;
            }
            setFile(selectedFile);
            setError(null);
            setResult(null);
        }
    };

    const handleUpload = async () => {
        if (!file) {
            setError('Please select a file first');
            return;
        }

        setUploading(true);
        setError(null);
        setResult(null);

        try {
            const formData = new FormData();
            formData.append('csvFile', file);

            const response = await fetch('/otgservices/uploadcampdata.php', {
                method: 'POST',
                body: formData,
            });

            const data = await response.json();

            if (data.success) {
                setResult(data);
                setFile(null);
                // Reset file input
                const fileInput = document.getElementById('csvFileInput');
                if (fileInput) {
                    fileInput.value = '';
                }
            } else {
                setError(data.message || 'Upload failed');
            }
        } catch (err) {
            setError('Network error: ' + err.message);
        } finally {
            setUploading(false);
        }
    };

    const resetForm = () => {
        setFile(null);
        setResult(null);
        setError(null);
        const fileInput = document.getElementById('csvFileInput');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    return (
        <div className="max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-lg">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Upload Camp Location Data</h2>

            <div className="space-y-4">
                <div>
                    <label htmlFor="csvFileInput" className="block text-sm font-medium text-gray-700 mb-2">
                        Select CSV File
                    </label>
                    <input
                        id="csvFileInput"
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 border border-gray-300 rounded-lg cursor-pointer"
                        disabled={uploading}
                    />
                    {file && (
                        <p className="mt-2 text-sm text-gray-600">
                            Selected: {file.name} ({Math.round(file.size / 1024)}KB)
                        </p>
                    )}
                </div>

                <div className="flex space-x-3">
                    <button
                        onClick={handleUpload}
                        disabled={!file || uploading}
                        className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                        {uploading ? (
                            <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Uploading...
              </span>
                        ) : (
                            'Upload CSV'
                        )}
                    </button>

                    <button
                        onClick={resetForm}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition-colors"
                    >
                        Reset
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-red-800">Error</h3>
                                <p className="mt-1 text-sm text-red-700">{error}</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Success Message */}
                {result && result.success && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <div className="flex">
                            <div className="flex-shrink-0">
                                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                            </div>
                            <div className="ml-3">
                                <h3 className="text-sm font-medium text-green-800">Upload Successful</h3>
                                <div className="mt-2 text-sm text-green-700">
                                    <p>{result.message}</p>
                                    {result.data && (
                                        <div className="mt-2 space-y-1">
                                            <p>• Total rows in CSV: {result.data.total_rows_in_csv}</p>
                                            <p>• Unique rows processed: {result.data.unique_rows_processed}</p>
                                            <p>• Database duplicates removed: {result.data.duplicates_removed_from_db}</p>
                                            <p>• New records inserted: {result.data.records_inserted}</p>
                                            <p>• Existing records updated: {result.data.records_updated}</p>
                                            <p>• Records skipped (no changes): {result.data.records_skipped}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Instructions */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">Instructions:</h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Upload a CSV file with camp location data</li>
                        <li>• Required columns: name, type, lat, long, icon_category, otg_route, import_status</li>
                        <li>• Optional column: description</li>
                        <li>• Duplicate locations (same lat/long) will be automatically handled</li>
                        <li>• Existing database duplicates will be cleaned up during upload</li>
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default CampDataUpload;